import { BreadcrumbsContainer } from '@/microfrontends/listing/shared/containers/BreadcrumbsContainer';
import { SEOBlock } from '@/microfrontends/listing/shared/containers/SEOBlockContainer';
import { VisitedOffersAnalytics } from '@/microfrontends/listing/shared/containers/VisitedOffersAnalytics';
import { ListingContextProvider } from '@/microfrontends/listing/shared/context/ListingContext';

import { useSearchOffersDesktopQuery } from '@/packages/query-state/shared/store/search-offers/v3/search-offers-desktop/query';
import { flatInfinityOffersSelector } from '@/packages/query-state/shared/store/search-offers/v3/search-offers-desktop/selectors';
import { memoWithDisplayName } from '@/packages/react-utils/memoWithDisplayName';
import { useDisablePageScroll } from '@/packages/react-utils/useDisablePageScroll';
import { useBrowserPageIsPageReloadingSelector } from '@/packages/redux-state/store/slices/browserPage';
import { useListingLayoutIsNarrowSelector } from '@/packages/redux-state/store/slices/listingLayout';
import { useListingLayoutGridTypeSelector } from '@/packages/redux-state/store/slices/listingLayout/selectors/dumb';
import { getIsBrowserBuild } from '@/packages/utils/isBuildType';

import { ListingLayout } from '../components/ListingLayout/ListingLayout';
import { ListingLoadErrorView } from '../components/ListingLoadErrorView';
import { ListingLoadingView } from '../components/ListingLoadingView';
import { ListingNoCards } from '../components/ListingNoCards';

import { ListingCardsContainer } from './ListingCardsContainer';
import { ListingPaginationContainer } from './ListingPaginationContainer';
import { SimilarOffersCardsContainer } from './SimilarOffersCardsContainer';

export const ListingContainer = memoWithDisplayName(() => {
  /**
   * Используем isRefetching вместо isFetching, чтобы отличать первоначальную загрузку от подгрузки офферов
   * isRefetching по сути равен isFetching && !isFetchingNextPage
   */
  const { data, isRefetching, refetch, status } = useSearchOffersDesktopQuery({ select: flatInfinityOffersSelector });

  const isPageReloading = useBrowserPageIsPageReloadingSelector();
  const isNarrow = useListingLayoutIsNarrowSelector();
  const gridType = useListingLayoutGridTypeSelector();

  const showLoader = (isRefetching || isPageReloading) && getIsBrowserBuild();

  useDisablePageScroll(showLoader);

  switch (status) {
    case 'loading':
      return <ListingLoadingView isNarrow={isNarrow} />;

    case 'error':
      return (
        <ListingLayout
          cards={null}
          isFetching={showLoader}
          isNarrow={isNarrow}
          navigation={null}
          noCardsDump={<ListingLoadErrorView isLoading={isRefetching} onRetry={refetch} />}
          pagination={null}
          seo={null}
          similarCards={null}
        />
      );

    case 'success': {
      const { offers, similarOffers } = data.pages[0];

      return (
        <ListingContextProvider value={data.pages[0]}>
          <ListingLayout
            cards={!!offers.length && <ListingCardsContainer />}
            gridType={gridType}
            isFetching={showLoader}
            isNarrow={isNarrow}
            navigation={<BreadcrumbsContainer />}
            noCardsDump={<ListingNoCards />}
            pagination={<ListingPaginationContainer />}
            seo={<SEOBlock />}
            similarCards={!!similarOffers && !showLoader && <SimilarOffersCardsContainer />}
          />
          <VisitedOffersAnalytics isFetchingOffers={isRefetching} offers={offers} />
        </ListingContextProvider>
      );
    }
  }
}, 'ListingContainer');
